import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import * as DS from '@styles/DesignSystem.styles';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const BaseButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'outline';
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  position: relative;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};

  /* Sizes */
  padding: ${props => {
    switch (props.$size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.md}`;
      case 'lg':
        return `${theme.spacing.md} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.sm} ${theme.spacing.lg}`;
    }
  }};

  font-size: ${props => {
    switch (props.$size) {
      case 'sm':
        return theme.typography.sizes.sm;
      case 'lg':
        return theme.typography.sizes.lg;
      default:
        return theme.typography.sizes.sm;
    }
  }};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StyledButtonPrimary = styled(DS.ButtonPrimary)<{
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};

  padding: ${props => {
    switch (props.$size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.md}`;
      case 'lg':
        return `${theme.spacing.md} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.sm} ${theme.spacing.lg}`;
    }
  }};

  font-size: ${props => {
    switch (props.$size) {
      case 'sm':
        return theme.typography.sizes.sm;
      case 'lg':
        return theme.typography.sizes.lg;
      default:
        return theme.typography.sizes.sm;
    }
  }};
`;

const StyledButtonSecondary = styled(DS.ButtonSecondary)<{
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};

  padding: ${props => {
    switch (props.$size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.md}`;
      case 'lg':
        return `${theme.spacing.md} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.sm} ${theme.spacing.lg}`;
    }
  }};

  font-size: ${props => {
    switch (props.$size) {
      case 'sm':
        return theme.typography.sizes.sm;
      case 'lg':
        return theme.typography.sizes.lg;
      default:
        return theme.typography.sizes.sm;
    }
  }};
`;

const StyledButtonTertiary = styled(DS.ButtonTertiary)<{
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
}>`
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};

  padding: ${props => {
    switch (props.$size) {
      case 'sm':
        return `${theme.spacing.xs} ${theme.spacing.md}`;
      case 'lg':
        return `${theme.spacing.md} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.sm} ${theme.spacing.lg}`;
    }
  }};

  font-size: ${props => {
    switch (props.$size) {
      case 'sm':
        return theme.typography.sizes.sm;
      case 'lg':
        return theme.typography.sizes.lg;
      default:
        return theme.typography.sizes.sm;
    }
  }};
`;

const GhostButton = styled(BaseButton)`
  background: transparent;
  border: none;
  color: ${theme.colors.primary};

  &:hover:not(:disabled) {
    background: ${theme.colors.surface};
  }
`;

const DangerButton = styled(BaseButton)`
  background: ${theme.colors.error};
  color: ${theme.colors.background};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OutlineButton = styled(BaseButton)`
  background: transparent;
  color: ${theme.colors.primary};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary};
    color: ${theme.colors.white};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  loading,
  disabled,
  icon,
  variant = 'primary',
  size = 'md',
  fullWidth,
  ...props
}) => {
  const getButtonComponent = () => {
    switch (variant) {
      case 'secondary':
        return StyledButtonSecondary;
      case 'tertiary':
        return StyledButtonTertiary;
      case 'ghost':
        return GhostButton;
      case 'danger':
        return DangerButton;
      case 'outline':
        return OutlineButton;
      default:
        return StyledButtonPrimary;
    }
  };

  const ButtonComponent = getButtonComponent();

  return (
    <ButtonComponent
      $size={size}
      $fullWidth={fullWidth}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </ButtonComponent>
  );
};
